import { postChatSessions } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { useRef, useState } from 'react';
import { popChatSession } from '../utils/chat-session-stash';

export type ChatSessionInfo = {
  session?: ChatSessionResponse;
  isLoading: boolean;
  error?: unknown;
  /**
   * Mint a fresh modal session (new sessionToken) for the chat session id by
   * calling POST /chat_sessions with `resumeChatSessionId`. Called explicitly
   * when the user submits a message — we don't auto-resume on mount, so the
   * dashboard can show persisted history without spinning up a sandbox.
   */
  resume: () => Promise<void>;
  /** Drop the current modal session — used after WS gives up reconnecting. */
  clear: () => void;
};

export const useChatSession = (chatSessionId: string | undefined, isLive: boolean | undefined): ChatSessionInfo => {
  const showErrorToast = useRequestErrorToast();
  const [session, setSession] = useState<ChatSessionResponse | undefined>(() => {
    if (!chatSessionId) return undefined;
    return popChatSession(chatSessionId);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>();
  const inflightRef = useRef<Promise<void> | null>(null);

  const fetchSession = async (id: string, sessionIsLive: boolean): Promise<void> => {
    setIsLoading(true);
    setError(undefined);
    try {
      const { data } = await postChatSessions({
        body: { resumeChatSessionId: id },
        headers: { 'x-is-live': String(sessionIsLive) },
        throwOnError: true,
      });
      setSession(data);
    } catch (err) {
      setError(err);
      showErrorToast(err);
    } finally {
      setIsLoading(false);
    }
  };

  const resume = async () => {
    if (!chatSessionId || isLive === undefined) return;
    if (inflightRef.current) {
      await inflightRef.current;
      return;
    }
    inflightRef.current = fetchSession(chatSessionId, isLive).finally(() => {
      inflightRef.current = null;
    });
    await inflightRef.current;
  };

  const clear = () => {
    setSession(undefined);
  };

  return { session, isLoading, error, resume, clear };
};
